import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Phone } from "lucide-react";
import { SITE, COMPLETED_PROJECTS } from "../data/site";
import { CITIES, CITY_LIST } from "../data/cities";
import { usePageMeta } from "../hooks/usePageMeta";

const CARD_IMAGES = [
  COMPLETED_PROJECTS[0].src,
  COMPLETED_PROJECTS[1].src,
  COMPLETED_PROJECTS[2].src,
  COMPLETED_PROJECTS[3].src,
  COMPLETED_PROJECTS[0].src,
  COMPLETED_PROJECTS[1].src,
  COMPLETED_PROJECTS[2].src,
  COMPLETED_PROJECTS[3].src,
];

export default function Locations() {
  usePageMeta({
    title: "Service Areas | Swift Luxury Walls — Tampa Bay & Sarasota",
    description:
      "Swift Luxury Walls designs and installs custom media walls, accent walls and entertainment centers across Tampa, Brandon, Riverview, Land O' Lakes, Clearwater, St. Petersburg, Sarasota and Bradenton.",
    canonicalPath: "/locations",
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-16 grain">
      <div className="container-x px-5 md:px-10 lg:px-16">
        {/* Breadcrumb */}
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#A1A1AA] mb-6">
          <Link to="/" className="hover:text-[#D4AF37]">
            Home
          </Link>{" "}
          / <span className="text-[#D4AF37]">Service Areas</span>
        </div>

        {/* Header */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">
            <MapPin className="w-3.5 h-3.5" /> Florida's Gulf Coast
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight mt-4">
            Custom media walls across Tampa Bay and Sarasota
          </h1>
          <p className="text-sm md:text-base text-[#D1D1D6] mt-5 leading-relaxed">
            Every city has different homes, materials and considerations. Choose your area to see how we approach a
            build there — or use the wall builder and we'll take it from any location in our service area.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/design-my-wall" className="btn-gold !py-3 !px-6" data-testid="locations-cta-design">
              Design My Wall <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${SITE.phoneDigits}`} className="btn-ghost !py-3 !px-6">
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
          </div>
        </div>

        {/* City grid */}
        <section className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CITY_LIST.map((slug, i) => {
            const c = CITIES[slug];
            return (
              <Link
                key={slug}
                to={`/locations/${slug}`}
                data-testid={`location-card-${slug}`}
                className="group relative rounded-2xl overflow-hidden border border-[#27272A] hover:border-[#AA8035] transition-colors bg-[#0d0d0d]"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={CARD_IMAGES[i % CARD_IMAGES.length]}
                    alt={`${c.name} — Swift Luxury Walls`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">Service area</div>
                  <div className="font-display text-2xl text-white mt-1">{c.name}, {c.state}</div>
                  <div className="text-xs text-[#D1D1D6] mt-2 line-clamp-2">
                    {c.primary_keyword}
                  </div>
                  <div className="text-[11px] tracking-wide text-[#D4AF37] mt-3 inline-flex items-center gap-1">
                    View {c.name} <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            );
          })}
        </section>

        {/* Coverage note */}
        <section className="mt-20 rounded-2xl border border-[#27272A] bg-[#0a0a0a] p-8 md:p-10 max-w-4xl">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">Broader coverage</div>
          <p className="text-sm md:text-base text-[#D1D1D6] leading-relaxed">
            Not on the list? We also work nearby communities including Valrico, Seffner, Wesley Chapel, Lutz, Odessa,
            Largo, Dunedin, Palm Harbor, Pinellas Park, Anna Maria Island and Lakewood Ranch. Reach out and we'll
            confirm scheduling in your area.
          </p>
        </section>
      </div>
    </div>
  );
}
